import { useEffect, useState } from "react";
import { HiOutlineArrowPath, HiOutlineSparkles } from "react-icons/hi2";
import { RemoteVersionInfo } from "src/config/version";

type VersionUpdateDialogProps = {
  localVersion: string;
  remoteVersion: RemoteVersionInfo;
  onReload: () => void;
  onSnooze: () => void;
  autoReloadSeconds?: number;
};

const VersionUpdateDialog = ({
  localVersion,
  remoteVersion,
  onReload,
  onSnooze,
  autoReloadSeconds = 15,
}: VersionUpdateDialogProps) => {
  const [secondsLeft, setSecondsLeft] = useState(autoReloadSeconds);

  useEffect(() => {
    if (secondsLeft <= 0) {
      onReload();
      return;
    }

    const timerId = window.setTimeout(() => {
      setSecondsLeft((value) => value - 1);
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [secondsLeft, onReload]);

  const progress = ((autoReloadSeconds - secondsLeft) / autoReloadSeconds) * 100;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="version-update-title"
      className="fixed inset-0 z-[99990] flex items-end justify-center bg-slate-950/50 p-4 backdrop-blur-sm sm:items-center"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-white/20 bg-white shadow-2xl">
        <div className="bg-gradient-to-r from-green-admin via-green-admin-light to-teal-600 px-6 py-5 text-white">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
              <HiOutlineSparkles className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">Actualización disponible</p>
              <h2 id="version-update-title" className="mt-1 text-xl font-bold leading-tight sm:text-2xl">
                Hay una versión nueva de ISF Cajeros
              </h2>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 sm:px-7">
          <p className="text-sm leading-relaxed text-gray-700 sm:text-base">
            Publicamos mejoras y correcciones. Recarga la página para usar la versión más reciente y evitar
            comportamientos desactualizados por caché del navegador.
          </p>

          <div className="mt-4 flex flex-wrap gap-2 text-xs font-medium">
            <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-600">Tu versión: v{localVersion}</span>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
              Nueva versión: v{remoteVersion.version}
            </span>
          </div>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between text-sm text-gray-600">
              <span>Recarga automática en</span>
              <span className="font-semibold text-gray-900">{secondsLeft}s</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-green-admin-light to-teal-500 transition-all duration-1000 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onReload}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-black"
            >
              <HiOutlineArrowPath className="h-5 w-5" aria-hidden />
              Actualizar ahora
            </button>
            <button
              type="button"
              onClick={onSnooze}
              className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Posponer 5 min
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionUpdateDialog;
